import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { SupplierSelector } from "@/shared/selectors";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery } from "@tanstack/react-query";
import {
	ColumnWidthCallbackParams,
	GridApi,
	ProcessCellForExportParams,
} from "ag-grid-community";
import { Moment } from "moment-timezone";
import { useState } from "react";
import { ChemistriesReportTable } from "./components/ChemistriesReportTable";
import {
	ELEMENTS,
	ElementSymbol,
	formatElementValue,
	isBelowDetection,
} from "./hooks/hooks";
import { ChemistryRow } from "./models/ChemistryRow";
import { getChemistryResults } from "./repositories/ChemistriesReportRepository";

const moment = getMomentTz();

const useChemistriesReport = ({
	heatId,
	supplierId,
	gradeId,
	startDate,
	endDate,
}: {
	heatId?: number | null;
	supplierId?: number | null;
	gradeId?: number | null;
	startDate?: Date | null;
	endDate?: Date | null;
}) =>
	useQuery<ChemistryRow[], Error>({
		queryKey: [
			"chemistry-results",
			heatId,
			supplierId,
			gradeId,
			startDate,
			endDate,
		],
		queryFn: ({ signal }) =>
			getChemistryResults(
				{ heatId, supplierId, gradeId, startDate, endDate },
				signal,
			),
	});

export const ChemistriesReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const [startDate, setStartDate] = useState<Moment | null>(
		moment().startOf("year"),
	);
	const [endDate, setEndDate] = useState<Moment | null>(moment().endOf("day"));
	const [supplier, setSupplier] = useState<{
		id: number | null;
		code: string | null;
	}>({ id: null, code: null });

	const { data: rows, isLoading } = useChemistriesReport({
		startDate: startDate?.toDate() ?? null,
		endDate: endDate?.toDate() ?? null,
	});

	const handleExportToExcel = () => {
		if (gridApi) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "chemistries_report.xlsx",
				sheetName: "Chemistries",
				freezeRows: "headers" as const,
				headerRowHeight: 24,
				rowHeight: 18,
				columnWidth: (widthParams: ColumnWidthCallbackParams) =>
					ELEMENTS.includes(widthParams.column?.getColId() as ElementSymbol)
						? 60
						: 110,
				processCellCallback: (cellParams: ProcessCellForExportParams) => {
					const colId = cellParams.column.getColId() as ElementSymbol;
					if (!ELEMENTS.includes(colId)) return cellParams.value;

					const belowDetection = (
						cellParams.node?.data as ChemistryRow | undefined
					)?.BelowDetection;
					return isBelowDetection(belowDetection, colId)
						? formatElementValue(cellParams.value, colId, belowDetection)
						: cellParams.value;
				},
			};
			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	return (
		<>
			<HelmetDexteel title="Chemistries Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Chemistries Report
					</Typography>
				</Grid2>

				<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="center">
					<Grid2 size={{ xs: 6, md: 1.5 }}>
						<DatePicker
							label="Start"
							format="MM/DD/YYYY"
							value={startDate}
							onChange={(newValue) => {
								setStartDate(newValue);
							}}
							slots={{
								textField: (params) => <TextField {...params} size="small" />,
							}}
							slotProps={{
								textField: {
									size: "small",
									margin: "none",
									sx: {
										backgroundColor: (theme) => theme.palette.background.paper,
									},
								},
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 6, md: 1.5 }}>
						<DatePicker
							label="End"
							format="MM/DD/YYYY"
							value={endDate}
							onChange={(newValue) => {
								setEndDate(newValue);
							}}
							slots={{
								textField: (params) => <TextField {...params} size="small" />,
							}}
							slotProps={{
								textField: {
									size: "small",
									margin: "none",
									sx: {
										backgroundColor: (theme) => theme.palette.background.paper,
									},
								},
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<SupplierSelector
							value={supplier.id}
							onChange={(id, code) => {
								setSupplier({ id, code: code ?? null });
								gridApi?.setGridOption("quickFilterText", code ?? "");
							}}
						/>
					</Grid2>
					<Grid2 size="grow" />
					<Grid2 size={{ xs: 12, md: 1 }}>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<ChemistriesReportTable
						isLoading={isLoading}
						rows={rows ?? []}
						setGridApi={setGridApi}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
